import { Injectable, inject } from '@angular/core';
import { OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { environment } from 'src/environments/environment';

/**
 * Autenticazione quando la SPA gira dentro un iframe di SugarCRM.
 *
 * Fuori dall'iframe questo servizio non fa nulla: `isSugarIframe` è `false` e resta valido il
 * normale flusso Keycloak (`angular-oauth2-oidc`). Dentro l'iframe non si può fare il redirect a
 * Keycloak (la CSP di Sugar blocca il framing), quindi:
 *   1. si chiede al padre il token Sugar via postMessage (handshake `CPQ_READY` → `CPQ_TOKEN`);
 *   2. si scambia il token Sugar con un token Keycloak sul backend (`/sugar-auth/token`);
 *   3. si seminano `access_token`/`id_token` nello storage OIDC così che il resto dell'app,
 *      guard e interceptor inclusi, si comporti come dopo un login normale.
 *
 * Il refresh è un nuovo handshake: il padre risponde a `CPQ_READY` on demand, quindi alla scadenza
 * si rifà l'intero giro invece di gestire un refresh_token.
 */
@Injectable({ providedIn: 'root' })
export class SugarAuthService {
	private oauth = inject(OAuthService);
	private storage = inject(OAuthStorage);

	/** Origini Sugar accettate come mittenti del token (whitelist per-ambiente). */
	private allowedOrigins = (environment.sugarIframe?.allowedOrigins ?? []).map((o) => o.replace(/\/$/, ''));

	/**
	 * `true` se la SPA è incorniciata **e** l'ambiente ha la configurazione Sugar: senza la
	 * seconda condizione un iframe qualsiasi (anteprima, portale, nesting) disabiliterebbe il
	 * login Keycloak senza avere un `tokenUrl` con cui rimpiazzarlo.
	 */
	readonly isSugarIframe = !!environment.sugarIframe && window.self !== window.top;

	/** Handshake in corso, condiviso: N richieste in 401 devono produrre un solo giro. */
	private inFlight?: Promise<void>;

	/**
	 * Ultimo token Sugar ottenuto dall'handshake, tenuto per le letture del CRM.
	 * Solo in memoria: è una credenziale del CRM e non va lasciata a riposo nello storage.
	 */
	private sugarToken?: string;

	/** Handshake + scambio + seeding. Usato all'avvio dentro l'iframe. */
	authenticate(): Promise<void> {
		return (this.inFlight ??= this.runAuthentication().finally(() => (this.inFlight = undefined)));
	}

	private async runAuthentication(): Promise<void> {
		const sugarToken = await this.handshake();
		this.sugarToken = sugarToken;
		const { accessToken, idToken, expiresIn } = await this.exchange(sugarToken);
		this.seed(accessToken, idToken, expiresIn);
		// Non c'è refresh_token da rinnovare: fermiamo il silent refresh della libreria, che
		// altrimenti alla scadenza fallirebbe e ci sbatterebbe fuori. Il refresh lo fa refresh().
		this.oauth.stopAutomaticRefresh();
	}

	/** Rifà l'intero flusso per ottenere token freschi (scadenza / 401). */
	refresh(): Promise<void> {
		return this.authenticate();
	}

	/** Se il token è ancora valido non fa nulla, altrimenti rifà il flusso. */
	async ensureAuthenticated(): Promise<void> {
		if (!this.oauth.hasValidAccessToken()) {
			await this.authenticate();
		}
	}

	/**
	 * Token Sugar da usare per leggere il CRM. Se non c'è (primo accesso o token invalidato)
	 * rifà l'intero giro di autenticazione, che è anche l'unico modo di riavere il token dal padre.
	 */
	async getSugarToken(): Promise<string> {
		// Fuori dall'iframe non c'è nessun padre che risponda all'handshake: senza questo controllo
		// si resterebbe fermi fino al timeout (20 secondi) per poi fallire comunque.
		if (!this.isSugarIframe) {
			throw new Error("Token SugarCRM disponibile solo dentro l'iframe di Sugar");
		}
		if (!this.sugarToken) {
			await this.authenticate();
			// authenticate() valorizza il token oppure lancia un errore:
			// il controllo serve a restringere il tipo a `string` per il return
			if (!this.sugarToken) {
				throw new Error('Nessun token SugarCRM disponibile');
			}
		}
		return this.sugarToken;
	}

	/**
	 * Da chiamare sulla `401` di Sugar con il token che l'ha presa: il prossimo
	 * {@link getSugarToken} rifà l'handshake invece di riproporre quello morto.
	 *
	 * Si butta solo se è ancora quello in uso: due letture in parallelo possono prendere la `401`
	 * a distanza di poco, e la seconda non deve scartare il token nuovo che la prima ha già ottenuto.
	 */
	invalidateSugarToken(expired: string): void {
		if (this.sugarToken === expired) {
			this.sugarToken = undefined;
		}
	}

	/**
	 * Chiede al padre Sugar il token: manda `CPQ_READY` e aspetta `CPQ_TOKEN` da un'origine in
	 * whitelist. Ripete il ping finché non arriva o scade il timeout — il padre potrebbe non essere
	 * ancora pronto al primo colpo.
	 */
	private handshake(): Promise<string> {
		return new Promise<string>((resolve, reject) => {
			const onMessage = (event: MessageEvent) => {
				if (!this.allowedOrigins.includes(event.origin)) return;
				const data = event.data;
				if (!data || data.type !== 'CPQ_TOKEN' || typeof data.access_token !== 'string') return;
				cleanup();
				resolve(data.access_token);
			};
			const ping = () => window.parent?.postMessage({ type: 'CPQ_READY' }, '*');
			const pinger = setInterval(ping, 2000);
			const timeout = setTimeout(() => {
				cleanup();
				reject(new Error('Nessun token da SugarCRM (timeout handshake)'));
			}, 20_000);
			const cleanup = () => {
				clearInterval(pinger);
				clearTimeout(timeout);
				window.removeEventListener('message', onMessage);
			};

			window.addEventListener('message', onMessage);
			ping();
		});
	}

	private async exchange(sugarToken: string): Promise<{ accessToken: string; idToken: string; expiresIn: number }> {
		const cfg = environment.sugarIframe;
		if (!cfg) throw new Error('Configurazione sugarIframe assente in questo ambiente');
		// fetch e non HttpClient: bypassa gli interceptor (auth/error) che qui non servono e che
		// rischierebbero di rientrare nel giro di refresh.
		const res = await fetch(cfg.tokenUrl, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ sugarToken }),
		});
		const payload = await res.json().catch(() => ({}));
		if (!res.ok) {
			throw new Error(payload?.error ?? `Scambio token fallito (HTTP ${res.status})`);
		}
		return payload;
	}

	/**
	 * Scrive i token nello storage OIDC con le chiavi che angular-oauth2-oidc si aspetta.
	 * Accoppiato al contratto di storage interno della libreria (angular-oauth2-oidc@14, che non
	 * espone un'API pubblica per adottare token esterni): rivedere le chiavi a ogni upgrade.
	 */
	private seed(accessToken: string, idToken: string, expiresIn: number): void {
		const now = Date.now();
		const idClaims = decodeJwtPayload(idToken) ?? {};
		const accessClaims = decodeJwtPayload(accessToken) ?? {};
		const accessExp = typeof accessClaims['exp'] === 'number' ? accessClaims['exp'] * 1000 : now + expiresIn * 1000;
		const idExp = typeof idClaims['exp'] === 'number' ? idClaims['exp'] * 1000 : accessExp;

		this.storage.setItem('access_token', accessToken);
		this.storage.setItem('access_token_stored_at', '' + now);
		this.storage.setItem('expires_at', '' + accessExp);
		this.storage.setItem('id_token', idToken);
		this.storage.setItem('id_token_claims_obj', JSON.stringify(idClaims));
		this.storage.setItem('id_token_expires_at', '' + idExp);
		this.storage.setItem('id_token_stored_at', '' + now);
		this.storage.setItem('granted_scopes', JSON.stringify(['openid']));
	}
}

/**
 * Overlay «autenticazione fallita» con pulsante di ricarica, l'unica via d'uscita dentro
 * l'iframe (nessun redirect a Keycloak possibile).
 *
 * È un elemento *aggiunto* al body e non un `body.innerHTML =`: quest'ultimo rimuoverebbe
 * `<app-root>` prima che Angular lo agganci (l'initializer gira prima del bootstrap) e il
 * bootstrap fallirebbe con NG0304. Il click è agganciato con `addEventListener` e non con un
 * attributo `onclick`, che una CSP senza `'unsafe-inline'` bloccherebbe.
 */
export function showSugarAuthError(): void {
	if (document.getElementById('sugar-auth-error')) return;

	const overlay = document.createElement('div');
	overlay.id = 'sugar-auth-error';
	overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;background:#fff;font-family:sans-serif;padding:2rem;text-align:center';
	overlay.textContent = 'Impossibile autenticarsi con SugarCRM.';

	const retry = document.createElement('button');
	retry.textContent = 'Riprova';
	retry.style.cssText = 'display:block;margin:1rem auto 0;padding:.5rem 1rem';
	retry.addEventListener('click', () => location.reload());

	overlay.appendChild(retry);
	document.body.appendChild(overlay);
}

/** Decodifica il payload di un JWT senza verificarne la firma. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
	try {
		const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
		return JSON.parse(decodeURIComponent(escape(atob(base64))));
	} catch {
		return null;
	}
}
